// Validation utility functions for input validation

/**
 * Filters input to only allow valid characters based on field type
 * @param {string} value - The input value
 * @param {string} fieldType - 'width', 'length', or 'quantity'
 * @returns {string} - Filtered string with only valid characters
 */
export const filterNumericInput = (value, fieldType) => {
  if (value === '' || value === null || value === undefined) {
    return '';
  }
  
  const str = String(value);
  
  if (fieldType === 'quantity') {
    // Quantity: only digits [0-9]
    return str.replace(/[^0-9]/g, '');
  } else {
    // Width/Length: digits [0-9] and 0 or 1 decimal point
    // Pattern: ^[0-9]*\.?[0-9]*$
    // Allow: "12", "12.", "12.5", ".5"
    // Filter out: "12.5.6", "abc", "-5"
    let filtered = str.replace(/[^0-9.]/g, '');
    // Ensure only one decimal point
    const parts = filtered.split('.');
    if (parts.length > 2) {
      filtered = parts[0] + '.' + parts.slice(1).join('');
    }
    return filtered;
  }
};

/**
 * Validates a single numeric field (width/length/quantity)
 * @param {string} value - The field value
 * @param {string} fieldType - 'width', 'length', or 'quantity'
 * @param {boolean} isEnabled - Whether the panel/stock sheet is enabled
 * @returns {{isValid: boolean, error: string|null}} - Validation result
 */
export const validateNumericField = (value, fieldType, isEnabled) => {
  // Empty allowed if not enabled
  if (!isEnabled) {
    return { isValid: true, error: null };
  }
  
  // Empty is invalid if enabled
  if (value === '' || value === null || value === undefined) {
    return { isValid: false, error: 'Field is required' };
  }
  
  // Check format for width/length (must match pattern for digits and 0 or 1 decimal)
  if (fieldType === 'width' || fieldType === 'length') {
    const decimalPattern = /^[0-9]*\.?[0-9]*$/;
    if (!decimalPattern.test(value)) {
      return { isValid: false, error: 'Invalid format. Only digits and one decimal point allowed.' };
    }
  }
  
  // Check format for quantity (must be digits only, no decimals)
  if (fieldType === 'quantity') {
    const integerPattern = /^[0-9]+$/;
    if (!integerPattern.test(value)) {
      return { isValid: false, error: 'Quantity must be a positive integer' };
    }
  }
  
  const numValue = parseFloat(value);
  
  // NaN is invalid
  if (isNaN(numValue)) {
    return { isValid: false, error: 'Invalid number' };
  }
  
  // Negative is invalid
  if (numValue < 0) {
    return { isValid: false, error: 'Value must be positive' };
  }
  
  // Quantity must be >= 1 and integer
  if (fieldType === 'quantity') {
    if (numValue < 1 || !Number.isInteger(numValue)) {
      return { isValid: false, error: 'Quantity must be at least 1' };
    }
  } else {
    // Width and length must be > 0
    if (numValue <= 0) {
      return { isValid: false, error: 'Value must be greater than 0' };
    }
  }
  
  return { isValid: true, error: null };
};

/**
 * Validates a single panel
 * @param {Object} panel - The panel object
 * @param {number} idx - The panel index
 * @param {boolean} differentMaterials - Whether different materials are enabled
 * @returns {{isValid: boolean, invalidFields: string[]}} - Validation result
 */
export const validatePanel = (panel, idx, differentMaterials = false) => {
  if (!panel.enabled) {
    return { isValid: true, invalidFields: [] };
  }

  const invalidFields = [];

  ['width', 'length', 'quantity'].forEach(field => {
    const validation = validateNumericField(panel[field], field, panel.enabled);
    if (!validation.isValid) {
      invalidFields.push(`${idx}-${field}`);
    }
  });

  // Validate material type if different materials is enabled
  if (differentMaterials) {
    if (!panel.materialType || panel.materialType === '') {
      invalidFields.push(`${idx}-materialType`);
    }
  }

  return {
    isValid: invalidFields.length === 0,
    invalidFields
  };
};

/**
 * Validates a single stock sheet
 * @param {Object} stock - The stock sheet object
 * @param {number} idx - The stock sheet index
 * @returns {{isValid: boolean, invalidFields: string[]}} - Validation result
 */
export const validateStockSheet = (stock, idx) => {
  if (!stock.enabled) {
    return { isValid: true, invalidFields: [] };
  }
  
  const invalidFields = [];
  
  ['width', 'length', 'quantity'].forEach(field => {
    const validation = validateNumericField(stock[field], field, stock.enabled);
    if (!validation.isValid) {
      invalidFields.push(`${idx}-${field}`);
    }
  });
  
  return {
    isValid: invalidFields.length === 0,
    invalidFields
  };
};

/**
 * Validates kerf thickness
 * @param {string} value - The kerf thickness value
 * @returns {{isValid: boolean, error: string|null}} - Validation result
 */
export const validateKerfThickness = (value) => {
  if (value === '' || value === null || value === undefined) {
    return { isValid: false, error: 'Kerf thickness is required' };
  }
  
  const numValue = parseFloat(value);
  
  if (isNaN(numValue)) {
    return { isValid: false, error: 'Invalid number' };
  }
  
  if (numValue < 0) {
    return { isValid: false, error: 'Kerf thickness must be >= 0' };
  }
  
  return { isValid: true, error: null };
};

/**
 * Comprehensive validation of all inputs
 * @param {Array} panels - Array of panel objects
 * @param {Array} stockSheets - Array of stock sheet objects
 * @param {string} kerfThickness - Kerf thickness value
 * @param {boolean} differentMaterials - Whether different materials are enabled
 * @returns {{isValid: boolean, errors: {kerf?: string, panels: string[], stockSheets: string[], summary: string}}} - Validation result
 */
export const validateAllInputs = (panels, stockSheets, kerfThickness, differentMaterials = false) => {
  const errors = {
    kerf: null,
    panels: [],
    stockSheets: [],
    summary: ''
  };

  // Validate kerf thickness
  const kerfValidation = validateKerfThickness(kerfThickness);
  if (!kerfValidation.isValid) {
    errors.kerf = kerfValidation.error;
  }

  // Validate all panels
  panels.forEach((panel, idx) => {
    const panelValidation = validatePanel(panel, idx, differentMaterials);
    if (!panelValidation.isValid) {
      errors.panels.push(...panelValidation.invalidFields);
    }
  });

  // Validate all stock sheets
  stockSheets.forEach((stock, idx) => {
    const stockValidation = validateStockSheet(stock, idx);
    if (!stockValidation.isValid) {
      errors.stockSheets.push(...stockValidation.invalidFields);
    }
  });

  // Check for at least one enabled panel
  const enabledPanels = panels.filter(p => p.enabled);
  if (enabledPanels.length === 0) {
    errors.summary = 'At least one enabled panel is required';
  }

  // Check for at least one enabled stock sheet
  const enabledStock = stockSheets.filter(s => s.enabled);
  if (enabledStock.length === 0) {
    if (errors.summary) {
      errors.summary += '; ';
    }
    errors.summary += 'At least one enabled stock sheet is required';
  }

  const isValid = !errors.kerf && 
                  errors.panels.length === 0 &&
                  errors.stockSheets.length === 0 &&
                  enabledPanels.length > 0 &&
                  enabledStock.length > 0;

  return { isValid, errors };
};

